import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AcademicYearBadge from "./AcademicYearBadge";

const API_BASE = 'https://admin-back-j3j4.onrender.com/api';
const DEPT_LIST_API = `${API_BASE}/department-user`;
const DEPT_DETAILS_API = deptId => `${API_BASE}/department-user/details/${deptId}`;

const yearSlots = {
  UG: ['I Year', 'II Year', 'III Year'],
  PG: ['I Year', 'II Year']
};

function DepartmentDetailsModel({ deptId }) {
  const [enrollmentDetails, setEnrollmentDetails] = useState([]);
  const [examinationDetails, setExaminationDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('enrollment');
  const [degreeLevel, setDegreeLevel] = useState('UG');
  const [academicYear, setAcademicYear] = useState('');
  const [currentYearSlot, setCurrentYearSlot] = useState(0);
  const [examResultType, setExamResultType] = useState('Student Appeared');
  const [allowedDegreeLevels, setAllowedDegreeLevels] = useState(['UG', 'PG']);

  // Masters
  const subcategories = ['General Category', 'PwBD', 'Muslim Minority', 'Other Minority'];
  const SUBCATEGORY_ORDER = ['General Category', 'PwBD', 'Muslim Minority', 'Other Minority']; // enforce order
  const categories = [
    'General Including EWS',
    'Scheduled Caste (SC)',
    'Scheduled Tribe (ST)',
    'Other Backward Classes (OBC)'
  ];
  const genders = ['Male', 'Female', 'Transgender'];

  useEffect(() => {
    setLoading(true);
    axios.get(DEPT_DETAILS_API(deptId), {
      headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
    })
      .then(res => {
        if (res.data.success) {
          setEnrollmentDetails(res.data.enrollmentDetails);
          setExaminationDetails(res.data.examinationDetails);
          setAcademicYear(res.data.academic_year || '');
          setAllowedDegreeLevels(res.data.degree_levels || ['UG', 'PG']);
          // Default to first allowed degree level
          setDegreeLevel((res.data.degree_levels && res.data.degree_levels[0]) || 'UG');
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [deptId]);

  // Helper to get year slots for current degree level (used for Enrollment)
  const getYearSlotsForLevel = () => (degreeLevel === 'UG' ? yearSlots.UG : yearSlots.PG);

  // Examination-specific single slot (match studentExamination behavior)
  const getExamYearSlot = () => (degreeLevel === 'UG' ? 'III Year' : 'II Year');

  // Normalization helper: safe lowercase trimmed string
  const norm = v => (v === null || v === undefined) ? '' : String(v).trim().toLowerCase();

  // Filtered details for summary (respect year slot and result type)
  const filteredDetails = (activeTab === 'enrollment' ? enrollmentDetails : examinationDetails)
    .filter(row => {
      if (!row) return false;
      if (row.degree_level && norm(row.degree_level) !== norm(degreeLevel)) return false;
      if (academicYear && row.academic_year && norm(row.academic_year) !== norm(academicYear)) return false;

      if (activeTab === 'enrollment') {
        const slots = getYearSlotsForLevel();
        const selectedSlot = slots[currentYearSlot] || slots[0];
        if (row.year && norm(row.year) !== norm(selectedSlot)) return false;
      } else {
        const examSlot = getExamYearSlot();
        if (row.year && norm(row.year) !== norm(examSlot)) return false;
        if (row.result_type && norm(row.result_type) !== norm(examResultType)) return false;
      }
      return true;
    });

  // NEW: Base rows for Overall Totals — ignore year slot and result type
  const rowsForTotals = React.useMemo(() => {
    const base = activeTab === 'enrollment' ? enrollmentDetails : examinationDetails;
    return (base || []).filter(row => {
      if (!row) return false;
      if (row.degree_level && norm(row.degree_level) !== norm(degreeLevel)) return false;
      if (academicYear && row.academic_year && norm(row.academic_year) !== norm(academicYear)) return false;
      // Do NOT filter by row.year or row.result_type here
      return true;
    });
  }, [activeTab, enrollmentDetails, examinationDetails, degreeLevel, academicYear]);

  // Summary calculation: subcategory -> { male, female, transgender }
  function getSummaryData() {
    const summary = {};
    const subcategories = ['General Category', 'PwBD', 'Muslim Minority', 'Other Minority'];
    const categories = [
      'General Including EWS',
      'Scheduled Caste (SC)',
      'Scheduled Tribe (ST)',
      'Other Backward Classes (OBC)'
    ];
    const norm = s => String(s || '').trim().toLowerCase();

    // init buckets
    subcategories.forEach(sub => (summary[sub] = { male: 0, female: 0, transgender: 0 }));

    filteredDetails.forEach(row => {
      // robust subcategory name
      const subName = row.subcategory || row.sub_category || row.subcategory_name || row.sub_name || row.sub || 'Unknown';
      if (!summary[subName]) summary[subName] = { male: 0, female: 0, transgender: 0 };

      // EXCLUDE "General Including EWS" from the four subcategory cards
      const catName = row.category || row.category_name || row.cate || '';
      if (norm(catName) === norm('General Including EWS')) return;

      const maleVal = Number(row.male ?? row.male_count ?? row.male_students ?? row.m ?? 0) || 0;
      const femaleVal = Number(row.female ?? row.female_count ?? row.female_students ?? row.f ?? 0) || 0;
      const transVal = Number(row.transgender ?? row.transgender_count ?? row.transgender_students ?? row.t ?? 0) || 0;

      if (maleVal || femaleVal || transVal) {
        summary[subName].male += maleVal;
        summary[subName].female += femaleVal;
        summary[subName].transgender += transVal;
      } else {
        const count = Number(row.count ?? row.total ?? 0) || 0;
        const g = norm(row.gender ?? row.gender_name ?? row.sex ?? '');
        if (count > 0 && g) {
          if (g.includes('male')) summary[subName].male += count;
          else if (g.includes('female')) summary[subName].female += count;
          else summary[subName].transgender += count;
        }
      }
    });

    return summary;
  }

  // For breakdown modal
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  function getSubcategoryBreakdown(subcategory) {
    const breakdownRows = filteredDetails.filter(row => {
      const s = row.subcategory || row.sub_category || row.subcategory_name || row.sub_name || row.sub || '';
      return norm(s) === norm(subcategory);
    });

    return categories.map(category => {
      let male = 0, female = 0, transgender = 0;
      breakdownRows.forEach(row => {
        const catName = row.category || row.category_name || row.cate || '';
        if (norm(catName) !== norm(category)) return;

        male += Number(row.male ?? row.male_count ?? 0) || 0;
        female += Number(row.female ?? row.female_count ?? 0) || 0;
        transgender += Number(row.transgender ?? row.transgender_count ?? 0) || 0;
      });

      return { category, male, female, transgender };
    });
  }

  if (loading) return (
    <div className="flex items-center justify-center h-40">
      <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-teal-500"></div>
    </div>
  );

  const summaryData = getSummaryData();

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <h1 className="text-2xl font-extrabold mb-6 text-gradient bg-gradient-to-r from-teal-600 via-blue-500 to-purple-600 bg-clip-text text-transparent">Department Details</h1>
      <div className="mb-8 flex justify-center">
        <div className="flex items-center gap-3 bg-gradient-to-r from-blue-50 via-teal-50 to-purple-50 border border-teal-200 rounded-2xl px-6 py-3 shadow-lg">
          <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Academic Year</span>
          <span className="text-lg font-bold text-teal-700 tracking-wide">{academicYear || <span className="text-gray-400">N/A</span>}</span>
        </div>
      </div>
      <div className="flex gap-6 mb-8 justify-center">
        <button
          className={`px-6 py-3 rounded-2xl shadow-lg font-semibold text-lg transition-all duration-200 border-2 ${activeTab === 'enrollment' ? 'bg-blue-600 text-white border-blue-600 scale-105' : 'bg-white text-blue-600 border-blue-200 hover:bg-blue-50'}`}
          onClick={() => setActiveTab('enrollment')}
        >Student Enrollment</button>
        <button
          className={`px-6 py-3 rounded-2xl shadow-lg font-semibold text-lg transition-all duration-200 border-2 ${activeTab === 'examination' ? 'bg-teal-600 text-white border-teal-600 scale-105' : 'bg-white text-teal-600 border-teal-200 hover:bg-teal-50'}`}
          onClick={() => setActiveTab('examination')}
        >Student Examination</button>
      </div>
      

      {/* Year slot (enrollment) and result type (examination) controls */}
      <div className="flex gap-6 mb-6 justify-center">
        {activeTab === 'enrollment' && (
          <div className="flex items-center gap-3">
            <div className="flex space-x-2">
              {getYearSlotsForLevel().map((slot, idx) => (
                <button
                  key={slot}
                  className={`px-6 py-2 rounded-lg font-semibold transition-colors border-2 ${
                    currentYearSlot === idx
                      ? 'bg-blue-600 text-white border-blue-600 shadow'
                      : 'bg-white text-blue-700 border-blue-200 hover:bg-blue-50'
                  }`}
                  onClick={() => setCurrentYearSlot(idx)}
                >
                  {slot}
                </button>
              ))}
            </div>
          </div>
        )}
        {activeTab === 'examination' && (
          <div className="flex items-center gap-3">
            <div className="flex space-x-2">
              {['Student Appeared', 'Student Passed Out', 'Student Above 60%'].map(type => (
                <button
                  key={type}
                  className={`px-6 py-2 rounded-lg font-semibold transition-colors border-2 ${
                    examResultType === type
                      ? 'bg-teal-600 text-white border-teal-600 shadow'
                      : 'bg-white text-teal-700 border-teal-200 hover:bg-teal-50'
                  }`}
                  onClick={() => setExamResultType(type)}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="rounded-3xl shadow-xl border border-blue-100 overflow-hidden bg-white">
        <div className="px-8 py-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <h3 className="text-xl font-bold text-blue-800">
            {activeTab === 'enrollment' ? 'Enrollment Summary' : 'Examination Summary'} ({degreeLevel})
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 px-8 py-6">
          {SUBCATEGORY_ORDER.map(subcategory => (
            <div
              key={subcategory}
              className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 cursor-pointer hover:shadow-xl transition"
              onClick={() => setEditingSubcategory(subcategory)}
            >
              <h4 className="font-bold text-lg text-blue-800 mb-4">{subcategory}</h4>
              <div className="flex justify-around text-center">
                <div>
                  <p className="text-2xl font-bold text-blue-600">{(summaryData[subcategory] && summaryData[subcategory].male) || 0}</p>
                  <p className="text-sm text-gray-500">Male</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-pink-600">{(summaryData[subcategory] && summaryData[subcategory].female) || 0}</p>
                  <p className="text-sm text-gray-500">Female</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-600">{(summaryData[subcategory] && summaryData[subcategory].transgender) || 0}</p>
                  <p className="text-sm text-gray-500">Transgender</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal for subcategory breakdown */}
      {editingSubcategory && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 bg-black/40 backdrop-blur-sm"
          onClick={() => setEditingSubcategory(null)}
          style={{ transition: 'all 0.3s' }}
        >
          <div
            className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-0 max-w-xl w-full mx-4 border border-blue-100 relative"
            onClick={e => e.stopPropagation()}
            style={{
              boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.18)',
              border: '1px solid rgba(255,255,255,0.18)'
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-8 py-6 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-3xl">
              <div className="flex items-center gap-3">
                <span className="text-3xl text-white">📊</span>
                <h3 className="text-xl font-bold text-white">
                  {editingSubcategory} - {degreeLevel} Breakdown
                  <div className="text-sm font-normal text-white/80">{activeTab === 'enrollment' ? getYearSlotsForLevel()[currentYearSlot] : examResultType}</div>
                </h3>
              </div>
              <button
                className="text-white text-2xl hover:text-blue-200 transition"
                onClick={() => setEditingSubcategory(null)}
                title="Close"
              >
                &times;
              </button>
            </div>
            {/* Table */}
            <div className="px-8 py-6">
              <table className="min-w-full text-base rounded-xl overflow-hidden shadow bg-white/90">
                <thead>
                  <tr className="bg-gradient-to-r from-blue-100 to-indigo-100">
                    <th className="px-4 py-2 text-left font-bold">Category</th>
                    <th className="px-4 py-2 font-bold text-blue-700">Male</th>
                    <th className="px-4 py-2 font-bold text-pink-700">Female</th>
                    <th className="px-4 py-2 font-bold text-purple-700">Transgender</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const rows = getSubcategoryBreakdown(editingSubcategory);
                    const hasData = rows.some(r => Number(r.male) > 0 || Number(r.female) > 0 || Number(r.transgender) > 0);
                    if (!hasData) {
                      return (
                        <tr>
                          <td colSpan={4} className="py-8 text-center text-gray-400">
                            <span className="mx-auto text-4xl mb-2 text-blue-200">📊</span>
                            <span>No data available for this subcategory and degree level.</span>
                          </td>
                        </tr>
                      );
                    }
                    return rows.map(row => (
                      <tr key={row.category} className="hover:bg-blue-50 transition">
                        <td className="px-4 py-2 font-semibold">{row.category}</td>
                        <td className="px-4 py-2 text-blue-700 font-bold">{row.male}</td>
                        <td className="px-4 py-2 text-pink-700 font-bold">{row.female}</td>
                        <td className="px-4 py-2 text-purple-700 font-bold">{row.transgender}</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
            <div className="px-8 pb-6">
              <button
                className="w-full py-2 bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 rounded-lg font-semibold hover:bg-blue-200 transition"
                onClick={() => setEditingSubcategory(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DepartmentDetails() {
  const [departmentUsers, setDepartmentUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDept, setSelectedDept] = useState(null);
  const [latestAcademicYear, setLatestAcademicYear] = useState("");

  useEffect(() => {
    axios.get(DEPT_LIST_API, {
      headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
    })
      .then(res => {
        setDepartmentUsers(res.data.users || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (departmentUsers.length > 0) {
      setLatestAcademicYear(departmentUsers[0].academic_year || "");
    }
  }, [departmentUsers]);

  // Get unique department names and ids
  const departments = Array.from(
    new Set(departmentUsers.map(u => JSON.stringify({ id: u.dept_id, name: u.department })))
  ).map(str => JSON.parse(str)).filter(d => d.name);

  if (loading) return (
    <div className="flex items-center justify-center h-40">
      <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-teal-500"></div>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <h1 className="text-3xl font-extrabold mb-8 text-gradient bg-gradient-to-r from-teal-600 via-blue-500 to-purple-600 bg-clip-text text-transparent">Department Details</h1>
      {/* Academic Year Badge */}
      <div className="flex justify-end mb-8">
        <AcademicYearBadge year={latestAcademicYear} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 mb-10">
        {departments.length === 0 ? (
          <div className="col-span-full text-center text-gray-400 py-8">No departments found.</div>
        ) : (
          departments.map(dept => (
            <button
              key={dept.id}
              className={`relative bg-gradient-to-br from-white via-blue-50 to-teal-50 p-6 rounded-3xl shadow-xl border-2 flex flex-col items-center cursor-pointer transition-all duration-200 ${
                selectedDept && selectedDept.id === dept.id
                  ? 'border-teal-500 ring-2 ring-teal-300 scale-105'
                  : 'border-gray-100 hover:border-teal-400'
              }`}
              onClick={() => setSelectedDept(dept)}
            >
              <span className="block text-xs uppercase tracking-widest text-teal-500 font-bold mb-1">Department</span>
              <h2 className="text-lg font-extrabold text-gray-800 mb-1 text-center">{dept.name}</h2>
              {selectedDept && selectedDept.id === dept.id && (
                <span className="absolute top-3 right-3 bg-teal-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow">
                  ✓
                </span>
              )}
            </button>
          ))
        )}
      </div>
      {/* Department Details Model */}
      {selectedDept && (
        <DepartmentDetailsModel deptId={selectedDept.id} />
      )}
    </div>
  );
}